import React, { useReducer, useEffect } from "react";
import Cookies from "js-cookie";
import axios2 from "axios";
import { BrowserRouter , Route , Routes } from "react-router-dom";
import { ConfigProvider, App as AppAntd } from "antd";
import dayjs, {Dayjs} from "dayjs";
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import jalaliPlugin from 'jalali-plugin-dayjs';
import updateLocale from 'dayjs/plugin/updateLocale';
import { JalaliLocaleListener } from "antd-jalali";
import { Helmet } from 'react-helmet-async';
import language from "./resources/languages/fa_IR";
import { AntDesign, ActionType, API, Enum } from "./resources/constants";

// Commons
import PendingRequest from './commons/pending-request'
import AuthRoute from './commons/auth-route'
import NoAuthRoute from './commons/no-auth-route'

// Interfaces
import { AxiosError } from "./interfaces/axios"
import { InternalAxiosRequestConfig, AxiosResponse } from "axios";

// Reducers
import GlobalReducer from './reducers/global'
import AxiosReducer from './reducers/axios'

// Contexts
import GlobalContext from './contexts/global'
import AxiosContext from './contexts/axios'

// Pages
import SignIn from "./pages/sign-in"
import SignUp from "./pages/sign-up"
import ResetPassword from "./pages/reset-password"
import Dashboard from "./pages/dashboard"
import Profile from "./pages/profile"
import NotFound from "./pages/not-found"
import NotAuth from "./pages/not-auth"
import Username from "./pages/username"

import User from "./pages/user-management/user"
import Role from "./pages/user-management/role"
import Permission from "./pages/user-management/permission"

import Host from "./pages/host-management/host"
import CURecordHost from "./pages/host-management/host/cu-record"

import City from "./pages/basic-information/city"
import CURecordCity from "./pages/basic-information/city/cu-record"
import Province from "./pages/basic-information/province"

import File from "./pages/file-management/file"
import Directory from "./pages/file-management/directory"

import Company from "./pages/ticket-management/company"
import CURecordCompany from "./pages/ticket-management/company/cu-record"

import Ticket from "./pages/ticket-management/ticket"
import CURecordTicket from "./pages/ticket-management/ticket/cu-record"

import Priority from "./pages/ticket-management/priority"
import CURecordPriority from "./pages/ticket-management/priority/cu-record"

import Price from "./pages/ticket-management/price"
import CURecordPrice from "./pages/ticket-management/price/cu-record"

// Layouts
import DashboardLayout from "./layouts/dashboard"

const { SET_PENDING_REQUESTS, SET_USER, SET_META, SET_ACCESS_TOKEN } = ActionType
const { USER_MANAGEMENT_PROFILE_MY_ACCOUNT, HOST_MANAGEMENT_DATA_META, FILE_MANAGEMENT_FILE_DOWNLOAD } = API;
const { Dayjs: DayjsLanguage } = language;
const { UsernameType } = Enum;

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(jalaliPlugin);
dayjs.extend(updateLocale);
dayjs.calendar('jalali');
dayjs.updateLocale('fa', DayjsLanguage);

export default function App() {

    const { protocol, hostname, port } = window.location;
    const host = port ? `${hostname}:${port}` : hostname;

    const [{
        user,
        meta,
        url,
        baseURL,
        accessToken,
    }, dispatchGlobal] = useReducer(GlobalReducer , {
        user: null,
        meta: null,
        accessToken: Cookies.get("accessToken"),
        url: `${protocol}//${host}/`,
        baseURL: process.env.NODE_ENV === 'development' ? `${protocol}//${hostname}:${process.env.REACT_APP_API_PORT}/` : `${protocol}//${hostname}/api/`,
    })

    const [{
        pendingRequests,
        axios,
    }, dispatchAxios] = useReducer(AxiosReducer , {
        pendingRequests: 0,
        axios: axios2.create({
            baseURL,
            timeout: Number(process.env.REACT_APP_TIME_OUT),
            withCredentials: true,
        })
    })

    const pendingRequest = (counter: number) => {
        const element = document.getElementById('pending-request')
        if (element) element.style.display = counter ? "flex" : "none";
    }

    useEffect(() => {
        let counter = 0;
        // All api request
        axios.interceptors.request.use(
            (config: InternalAxiosRequestConfig) => {
                //dispatchAxios({ type: SET_PENDING_REQUESTS , payload: { plus: true }})
                pendingRequest(++counter)
                const accessToken = Cookies.get("accessToken")
                config.headers.Authorization = accessToken && `Bearer ${accessToken}`
                return config
            },
            (error: any) => Promise.reject(error)
        );
        // All api response
        axios.interceptors.response.use(
            (response: AxiosResponse ) => {
                //dispatchAxios({ type: SET_PENDING_REQUESTS , payload: { plus: false }})
                pendingRequest(--counter)
                return response
            },
            (error: AxiosError) => {
                const { response: { status } } = error
                if (status === 401) {
                    Cookies.remove('accessToken')
                    dispatchGlobal({ type: SET_ACCESS_TOKEN , payload: { accessToken: null }})
                    dispatchGlobal({ type: SET_USER , payload: { user: null }})
                }

                //dispatchAxios({ type: SET_PENDING_REQUESTS , payload: { plus: false }})
                pendingRequest(--counter)
                return Promise.reject(error)
            }
        );
        axios.get(HOST_MANAGEMENT_DATA_META).then((response: { data: {} }) => {
            const { data: meta } = response
            dispatchGlobal({ type: SET_META , payload: { meta }})
        }).catch( (error: {}) => error );
        axios.get(USER_MANAGEMENT_PROFILE_MY_ACCOUNT).then((response: { data: { complete: boolean } }) => {
            const { data: user } = response
            dispatchGlobal({ type: SET_USER , payload: { user }})
        }).catch( (error: {}) => error );
    }, [])

    return (
        <ConfigProvider
            direction={"rtl"}
            locale={language}
            theme={AntDesign.theme}
        >
            <AppAntd>
            <JalaliLocaleListener />
                <PendingRequest pendingRequests={pendingRequests} />
                <Helmet>
                    { meta?.title && <title>{meta.title}</title> }
                    { meta?.favicon && <link rel="icon" href={baseURL + FILE_MANAGEMENT_FILE_DOWNLOAD + meta?.favicon?.id} /> }
                </Helmet>
                <GlobalContext.Provider value={{
                user,
                meta,
                url,
                baseURL,
                accessToken,
                dispatch: dispatchGlobal
            }}>
                <AxiosContext.Provider value={{
                    pendingRequests,
                    axios,
                    dispatch: dispatchAxios
                }}>
                    <BrowserRouter basename={process.env.NODE_ENV === 'development' ? '/' : '/'}>
                        <Routes>
                            <Route path="/" element={<DashboardLayout />}>
                                <Route index element={<AuthRoute><Dashboard /></AuthRoute>} />
                                <Route path="user-management">
                                    <Route index element={<NotFound />} />
                                    <Route path="user">
                                        <Route index element={<AuthRoute roles={["HOST_MANAGEMENT", "USER_MANAGEMENT"]}><User /></AuthRoute>} />
                                    </Route>
                                    <Route path="role">
                                        <Route index element={<AuthRoute roles={["HOST_MANAGEMENT"]}><Role /></AuthRoute>} />
                                    </Route>
                                    <Route path="permission">
                                        <Route index element={<AuthRoute roles={["HOST_MANAGEMENT"]}><Permission /></AuthRoute>} />
                                    </Route>
                                </Route>
                                <Route path="host-management">
                                    <Route index element={<NotFound />} />
                                    <Route path="host">
                                        <Route index element={<AuthRoute roles={["HOST_MANAGEMENT"]}><Host /></AuthRoute>} />
                                        <Route path="add" element={<AuthRoute roles={["HOST_MANAGEMENT"]}><CURecordHost /></AuthRoute>} />
                                        <Route path="edit/:id" element={<AuthRoute roles={["HOST_MANAGEMENT"]}><CURecordHost /></AuthRoute>} />
                                    </Route>
                                </Route>
                                <Route path="file-management">
                                    <Route index element={<NotFound />} />
                                    <Route path="file">
                                        <Route index element={<AuthRoute roles={["FILE_MANAGEMENT"]}><File /></AuthRoute>} />
                                    </Route>
                                    <Route path="directory">
                                        <Route index element={<AuthRoute roles={["FILE_MANAGEMENT"]}><Directory /></AuthRoute>} />
                                    </Route>
                                </Route>
                                <Route path="basic-information">
                                    <Route index element={<NotFound />} />
                                    <Route path="city">
                                        <Route index element={<AuthRoute roles={["BASIC_INFORMATION"]}><City /></AuthRoute>} />
                                        <Route path="add" element={<AuthRoute roles={["BASIC_INFORMATION"]}><CURecordCity /></AuthRoute>} />
                                        <Route path="edit/:id" element={<AuthRoute roles={["BASIC_INFORMATION"]}><CURecordCity /></AuthRoute>} />
                                    </Route>
                                    <Route path="province">
                                        <Route index element={<AuthRoute roles={["BASIC_INFORMATION"]}><Province /></AuthRoute>} />
                                    </Route>
                                </Route>
                                <Route path="ticket-management">
                                    <Route index element={<NotFound />} />
                                    <Route path="company">
                                        <Route index element={<AuthRoute roles={["TICKET_MANAGEMENT"]}><Company /></AuthRoute>} />
                                        <Route path="add" element={<AuthRoute roles={["TICKET_MANAGEMENT"]}><CURecordCompany /></AuthRoute>} />
                                        <Route path="edit/:id" element={<AuthRoute roles={["TICKET_MANAGEMENT"]}><CURecordCompany /></AuthRoute>} />
                                    </Route>
                                    <Route path="ticket">
                                        <Route index element={<AuthRoute roles={["TICKET_MANAGEMENT"]}><Ticket /></AuthRoute>} />
                                        <Route path="add" element={<AuthRoute roles={["TICKET_MANAGEMENT"]}><CURecordTicket /></AuthRoute>} />
                                        <Route path="edit/:id" element={<AuthRoute roles={["TICKET_MANAGEMENT"]}><CURecordTicket /></AuthRoute>} />
                                    </Route>
                                    <Route path="price">
                                        <Route index element={<AuthRoute roles={["TICKET_MANAGEMENT"]}><Price /></AuthRoute>} />
                                        <Route path="add" element={<AuthRoute roles={["TICKET_MANAGEMENT"]}><CURecordPrice /></AuthRoute>} />
                                        <Route path="edit/:id" element={<AuthRoute roles={["TICKET_MANAGEMENT"]}><CURecordPrice /></AuthRoute>} />
                                    </Route>
                                    <Route path="priority">
                                        <Route index element={<AuthRoute roles={["TICKET_MANAGEMENT"]}><Priority /></AuthRoute>} />
                                        <Route path="add" element={<AuthRoute roles={["TICKET_MANAGEMENT"]}><CURecordPriority /></AuthRoute>} />
                                        <Route path="edit/:id" element={<AuthRoute roles={["TICKET_MANAGEMENT"]}><CURecordPriority /></AuthRoute>} />
                                    </Route>
                                </Route>
                                <Route path="profile" element={<AuthRoute><Profile /></AuthRoute>} />
                                <Route path="mobile" element={<AuthRoute><Username type={UsernameType.Mobile} /></AuthRoute>} />
                                <Route path="email" element={<AuthRoute><Username type={UsernameType.Email} /></AuthRoute>} />
                                <Route path="sign-in" element={<NoAuthRoute><SignIn /></NoAuthRoute>} />
                                <Route path="sign-up" element={<NoAuthRoute><SignUp /></NoAuthRoute>} />
                                <Route path="reset-password" element={<NoAuthRoute><ResetPassword /></NoAuthRoute>} >
                                    <Route path=":token" />
                                 </Route>
                                <Route path="not-auth" element={<AuthRoute><NotAuth /></AuthRoute>} />
                                <Route path="*" element={<NotFound />} />
                            </Route>
                        </Routes>
                    </BrowserRouter>
                </AxiosContext.Provider>
            </GlobalContext.Provider>
            </AppAntd>
        </ConfigProvider>
    )
}
